/**
 * Candidate-facing API client for the CI4/MySQL API (signup/login/session,
 * candidate_profiles, showcase_profiles, and the public profile-by-slug
 * lookup). Mirrors admin-api.ts's pattern: functions return the SAME TS
 * shapes the old localStorage modules (candidate.ts, showcase.ts) used, so
 * existing rendering code needs minimal changes.
 */
import type { CandidateProfile } from "@/components/site/candidate";
import type { ShowcaseProfile } from "@/components/site/showcase";
import type { AutoApplyCandidateState, AutoApplyPrefs, AutoSubmission } from "@/components/site/autoapply";

const API_URL = (import.meta.env["VITE_API_URL"] as string | undefined)?.replace(/\/$/, "") ?? "";

const TOKEN_KEY = "closdd.candidate.token";

export function getCandidateToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setCandidateToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

/** Carries the HTTP status so callers can tell "token is actually invalid" (401) apart from a transient server/network hiccup. */
class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getCandidateToken();
  const res = await fetch(`${API_URL}/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(body?.messages?.error ?? body?.error ?? `Request failed (${res.status})`, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** No-throw fetch, for public/unauthenticated lookups where a 404 is expected/normal. */
async function publicFetch<T>(path: string): Promise<{ ok: boolean; status: number; data: T | null }> {
  const res = await fetch(`${API_URL}/api${path}`);
  if (!res.ok) return { ok: false, status: res.status, data: null };
  return { ok: true, status: res.status, data: (await res.json()) as T };
}

/**
 * Uploads a recorded pitch/assessment answer, video intro or CV and returns
 * its real, permanent URL. Recordings used to be saved as a
 * URL.createObjectURL() blob: reference straight into the database — that
 * only ever played back in the same browser tab that made it, so this is
 * what actually makes a pitch audible to anyone else.
 */
export async function uploadFile(file: Blob, filename: string): Promise<string> {
  const token = getCandidateToken();
  const form = new FormData();
  form.append("file", file, filename);
  const res = await fetch(`${API_URL}/api/me/uploads`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.messages?.error ?? body?.error ?? `Upload failed (${res.status})`);
  }
  const data = (await res.json()) as { url: string };
  return data.url;
}

/* ------------------------------------------------------------------ *
 * Auth
 * ------------------------------------------------------------------ */

export type CandidateUser = { id: number; email: string; role: "candidate" };

export async function candidateSignup(name: string, email: string, password: string): Promise<CandidateUser> {
  const body = await authFetch<{ token: string; user: CandidateUser }>("/auth/signup", {
    method: "POST",
    body: JSON.stringify({ name, email, password }),
  });
  setCandidateToken(body.token);
  return body.user;
}

export async function candidateLogin(email: string, password: string): Promise<CandidateUser> {
  const body = await authFetch<{ token: string; user: CandidateUser }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, role: "candidate" }),
  });
  setCandidateToken(body.token);
  return body.user;
}

/**
 * Only a 401 means the token itself is actually invalid/expired — anything
 * else (a 500, a dropped connection) is a transient failure, and clearing a
 * still-good token on one of those was silently signing candidates out of
 * a session that was otherwise fine.
 */
export async function getCandidateSession(): Promise<CandidateUser | null> {
  const token = getCandidateToken();
  if (!token) return null;
  try {
    const body = await authFetch<{ user: CandidateUser }>("/auth/me");
    return body.user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      setCandidateToken(null);
    }
    return null;
  }
}

export async function candidateLogout(): Promise<void> {
  const token = getCandidateToken();
  setCandidateToken(null);
  if (!token) return;
  try {
    await fetch(`${API_URL}/api/auth/logout`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
  } catch {
    // token already cleared client-side
  }
}

export async function updatePasswordApi(current: string, next: string): Promise<void> {
  await authFetch<void>("/me/password", { method: "POST", body: JSON.stringify({ current, next }) });
}

/**
 * Always resolves, even if the email doesn't match an account — the API
 * responds the same way either way so this can't be used to enumerate
 * accounts.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await authFetch<void>("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await authFetch<void>("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
}

/* ------------------------------------------------------------------ *
 * Own profile + showcase
 * ------------------------------------------------------------------ */

export async function fetchMyProfile(): Promise<CandidateProfile> {
  const { profile } = await authFetch<{ profile: CandidateProfile }>("/me/profile");
  return profile;
}

/** Sends the welcome email (once) — see Api\Me\Welcome. Called by /signup after the profile and pitch are saved. */
export async function sendWelcomeEmail(): Promise<void> {
  await authFetch("/me/welcome", { method: "POST" });
}

export async function updateMyProfile(patch: Partial<CandidateProfile>): Promise<CandidateProfile> {
  const { profile } = await authFetch<{ profile: CandidateProfile }>("/me/profile", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  return profile;
}

export async function fetchMyShowcase(): Promise<ShowcaseProfile> {
  const { showcase } = await authFetch<{ showcase: ShowcaseProfile }>("/me/showcase");
  return showcase;
}

export async function updateMyShowcase(patch: Partial<ShowcaseProfile>): Promise<ShowcaseProfile> {
  const { showcase } = await authFetch<{ showcase: ShowcaseProfile }>("/me/showcase", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  return showcase;
}

/* ------------------------------------------------------------------ *
 * Applications
 * ------------------------------------------------------------------ */

export type MyApplication = { id: string; jobId: string; status: string; appliedAt: string; createdByDistribution?: boolean };

export async function fetchMyApplications(): Promise<MyApplication[]> {
  const { applications } = await authFetch<{ applications: MyApplication[] }>("/me/applications");
  return applications;
}

/**
 * Applies to a real (MySQL-backed) job by its numeric id. Applying twice to
 * the same role is safe — the API returns the existing application instead
 * of erroring.
 */
export async function applyToJob(jobId: string): Promise<MyApplication> {
  const { application } = await authFetch<{ application: MyApplication }>("/me/applications", {
    method: "POST",
    body: JSON.stringify({ jobId: Number(jobId) }),
  });
  return application;
}

export type SavedJob = {
  jobId: string;
  savedAt: string;
  title: string;
  company: string;
  location: string;
  setting: string;
  ref: string | null;
  /** Still Live, approved and shown on the site — closed roles can only be removed. */
  open: boolean;
};

export async function fetchSavedJobs(): Promise<SavedJob[]> {
  const { saved } = await authFetch<{ saved: SavedJob[] }>("/me/saved-jobs");
  return saved;
}

export async function saveJob(jobId: string): Promise<void> {
  await authFetch("/me/saved-jobs", { method: "POST", body: JSON.stringify({ jobId: Number(jobId) }) });
}

export async function unsaveJob(jobId: string): Promise<void> {
  await authFetch(`/me/saved-jobs/${encodeURIComponent(jobId)}`, { method: "DELETE" });
}

/* ------------------------------------------------------------------ *
 * Public — no auth
 * ------------------------------------------------------------------ */

export type PublicCandidate = { name: string; email: string; phone: string; cvUrl?: string; cvName?: string };

export async function fetchPublicProfile(
  slug: string,
): Promise<{ showcase: ShowcaseProfile; candidate: PublicCandidate } | null> {
  const res = await publicFetch<{ showcase: ShowcaseProfile; candidate: PublicCandidate }>(
    `/public/profiles/${encodeURIComponent(slug)}`,
  );
  return res.data;
}

export async function isSlugTaken(slug: string): Promise<boolean> {
  const res = await publicFetch<{ taken: boolean }>(`/public/slug-check?slug=${encodeURIComponent(slug)}`);
  return res.data?.taken ?? false;
}

/* ------------------------------------------------------------------ *
 * "Find me more jobs"
 * ------------------------------------------------------------------ */

export async function fetchMyAutoApply(): Promise<{ candidate: AutoApplyCandidateState; submissions: AutoSubmission[] }> {
  return authFetch<{ candidate: AutoApplyCandidateState; submissions: AutoSubmission[] }>("/me/autoapply");
}

/**
 * Saving enabled+permission together for the first time also runs an
 * initial match server-side — newSubmissions carries whatever it found.
 */
export async function updateMyAutoApply(
  patch: Partial<{ enabled: boolean; permission: boolean; prefs: AutoApplyPrefs }>,
): Promise<{ candidate: AutoApplyCandidateState; newSubmissions: AutoSubmission[] }> {
  return authFetch<{ candidate: AutoApplyCandidateState; newSubmissions: AutoSubmission[] }>("/me/autoapply", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}
